import Vue from 'vue'
import Vuex from 'vuex'
import ReconnectingWebSocket from 'reconnecting-websocket'

Vue.use(Vuex)

const VERSION = '1.0'

class CartItem {
  constructor (product, attributes, quantity) {
    this.product = product
    this.attributes = attributes
    this.quantity = quantity
  }
}

function setItem (key, payload) {
  window.localStorage.setItem(key, JSON.stringify(payload))
}

function getItem (key) {
  const payloadString = window.localStorage.getItem(key)
  if (!payloadString) {
    return null
  }

  try {
    return JSON.parse(payloadString)
  } catch (err) {
    console.log(err)
    return null
  }
}

// validate store version.
const currentVersion = getItem('version')
if (!currentVersion || currentVersion < VERSION) {
  window.localStorage.clear()
  setItem('version', VERSION)
}

// Mirror of utils.js isMonthlyOptionAllowed — kept inline to avoid circular import
// (utils.js imports this store). Both must agree on the unlock contract.
function isMonthlyAllowed () {
  const isPrivate = Vue.config.MONTHLY_OPTION_PRIVATE === true || Vue.config.MONTHLY_OPTION_PRIVATE === 'true'
  if (!isPrivate) return true
  const search = (typeof window !== 'undefined' && window.location && window.location.search) || ''
  const urlUnlocked = /[?&]monthly_option_enabled=true(?:&|$)/.test(search)
  if (typeof window === 'undefined' || !window.sessionStorage) return urlUnlocked
  try {
    if (urlUnlocked) {
      window.sessionStorage.setItem('monthly_option_enabled', 'true')
      return true
    }
    return window.sessionStorage.getItem('monthly_option_enabled') === 'true'
  } catch (e) {
    return urlUnlocked
  }
}

function gatedDonateType (value) {
  return value === 'monthly' && !isMonthlyAllowed() ? 'onetime' : value
}

const store = new Vuex.Store({
  state: {
    user: {},
    token: '',
    products: {},
    productsList: new Set(),
    cart: [],
    checkoutInfo: {},
    offlineShipping: false,
    showCartStatusComponent: true,
    ws: {},
    clientId: null,
    paymentMethod: null,
    orderId: null,
    publicOrderId: null,
    orderIdTimestamp: null,
    paypalPaymentId: '',
    isNavFocusMode: false,
    navTitle: '',
    backVisible: true,
    previousRoute: null,
    routeBeforeCheckout: null,
    facebookAuth: {},
    selectedPage: {},
    donateType: null,
    forceDaily: false,
    totalCount: 0
  },
  mutations: {
    setUser (state, user) {
      state.user = user
      setItem('user', user)
      if (user && window._opbeat) {
        try {
          window._opbeat('setUserContext', {
            username: (user.firstName + ' ' + user.lastName).trim() || user.displayName,
            email: user.email,
            id: user.uuid || user.id
          })
        } catch (err) {
          console.log(err)
        }
      }
      if (user && window.Raven) {
        try {
          window.Raven.setUserContext({
            id: user.uuid || user.id,
            email: user.email
          })

          window.Raven.setExtraContext({
            fullName: (user.firstName + ' ' + user.lastName).trim() || user.displayName,
            fbid: user.fbid
          })

          window.Raven.captureMessage(`SetUser ${user.uuid} / ${user.email}`, {
            level: 'info',
            fingerprint: ['SetUser']
          })
        } catch (err) {
          console.log(err)
        }
      }
    },
    setToken (state, token) {
      state.token = token
      setItem('token', token)
      Vue.http.headers.common['Authorization'] = `Bearer ${token}`
    },
    clearProducts (state) {
      state.products = {}
      state.productsList = new Set()
    },
    addProduct (state, product) {
      state.products[product.sku] = product
      if (product.listed) {
        state.productsList.add(product.sku)
      }
    },
    initCart (state) {
      state.paymentMethod = getItem('paymentMethod')
      state.cart = getItem('cart') || []
      state.cart = state.cart.filter(item => item.quantity > 0 && Vue.prototype.$utils.isProductAvailable(item.product))
      state.orderId = getItem('orderId')
      state.publicOrderId = getItem('publicOrderId')
      state.orderIdTimestamp = new Date(getItem('orderIdTimestamp'))

      state.selectedPage = getItem('selectedPage') || {}
      state.routeBeforeCheckout = getItem('routeBeforeCheckout')
    },
    addToCart (state, payload) {
      let skuA = Vue.prototype.$utils.getProductConfigSku(payload.product, payload.attributes)
      let matched = state.cart.find(item => {
        let skuB = Vue.prototype.$utils.getProductConfigSku(item.product, item.attributes)
        return skuA === skuB
      })

      // Force product donate can add only 1.
      if (payload.product.type === Vue.prototype.$const.PRODUCT_TYPES.DONATE) {
        payload.quantity = 1
      }

      if (matched) {
        // Donate type should update customized product's price too.
        if (matched.product.type === Vue.prototype.$const.PRODUCT_TYPES.DONATE) {
          matched.quantity = payload.quantity
          matched.product.price = payload.product.price
        } else {
          matched.quantity += payload.quantity
        }
        matched.attributes = Object.assign({}, payload.attributes)
      } else {
        let cartItem = new CartItem(payload.product, Object.assign({}, payload.attributes), payload.quantity)
        state.cart.push(cartItem)
      }

      setItem('cart', state.cart)
    },
    updateCartProduct (state, product) {
      let matched = state.cart.find(item => item.product.sku === product.sku)
      if (matched) {
        const price = matched.product.price
        matched.product = product
        if (product.type === Vue.prototype.$const.PRODUCT_TYPES.DONATE) {
          if (price) {
            matched.product.price = price
          }
        }
      }
    },
    removeFromCart (state, payload) {
      let skuA = Vue.prototype.$utils.getProductConfigSku(payload.product, payload.attributes)
      let index = state.cart.findIndex(item => {
        let skuB = Vue.prototype.$utils.getProductConfigSku(item.product, item.attributes)
        return skuA === skuB
      })

      if (index !== -1) {
        state.cart.splice(index, 1)
        setItem('cart', state.cart)
      }
    },
    clearCart (state) {
      state.cart = []
      setItem('cart', state.cart)
    },
    clearSelectedPage (state) {
      state.selectedPage = null
      setItem('selectedPage', state.selectedPage)
    },
    increaseProductQuantity (state, product) {
      let matched = state.cart.find(item => item.product.sku === product.sku)
      if (matched) {
        matched.quantity += 1
        setItem('cart', state.cart)
      }
    },
    decreaseProductQuantity (state, product) {
      let matched = state.cart.find(item => item.product.sku === product.sku)
      if (matched) {
        matched.quantity -= 1
        setItem('cart', state.cart)
      }
    },
    updateProductQuantity (state, payload) {
      let skuA = Vue.prototype.$utils.getProductConfigSku(payload.product, payload.attributes)
      let matched = state.cart.find(item => {
        let skuB = Vue.prototype.$utils.getProductConfigSku(item.product, item.attributes)
        return skuA === skuB
      })

      if (matched) {
        matched.quantity = payload.quantity
        setItem('cart', state.cart)
      }
    },
    setOfflineShipping (state, value) {
      state.offlineShipping = value
      setItem('offlineShipping', state.offlineShipping)
    },
    updateCheckoutInfo (state, payload) {
      state.checkoutInfo = payload
      setItem('checkoutInfo', state.checkoutInfo)
    },
    toggleCartStatus (state, enabled) {
      state.showCartStatusComponent = enabled
    },
    setToast (state, toast) {
      state.toast = toast
    },
    setWebSocket (state, ws) {
      state.ws = ws
    },
    setPaymentMethod (state, method) {
      state.paymentMethod = method
      setItem('paymentMethod', method)
    },
    setClientId (state, clientId) {
      state.clientId = clientId
      setItem('clientId', clientId)
    },
    setOmiseTokenId (state, tokenId) {
      // DEPRECATED: not use for now
      state.omiseTokenId = tokenId
      setItem('omiseTokenId', tokenId)
    },
    setOrderId (state, payload) {
      if (payload === null) {
        state.orderId = null
        state.publicOrderId = null
        state.orderIdTimestamp = null
        setItem('orderId', null)
        setItem('publicOrderId', null)
        setItem('orderIdTimestamp', null)
      } else {
        const { orderId, publicOrderId } = payload
        state.orderId = orderId
        state.publicOrderId = publicOrderId || orderId
        state.orderIdTimestamp = new Date()
        setItem('orderId', orderId)
        setItem('publicOrderId', state.publicOrderId)
        setItem('orderIdTimestamp', state.orderIdTimestamp)
      }
    },
    setPaypalPaymentId (state, paypalPaymentId) {
      state.paypalPaymentId = paypalPaymentId
      setItem('paypalPaymentId', paypalPaymentId)
    },
    setNavFocusMode (state, enabled) {
      state.isNavFocusMode = enabled
    },
    setNavTitle (state, title) {
      state.navTitle = title
    },
    setBackVisibility (state, enabled) {
      state.backVisible = enabled
    },
    setPreviousRoute (state, route) {
      state.previousRoute = route
    },
    setRouteBeforeCheckout (state, route) {
      state.routeBeforeCheckout = route.fullPath
      setItem('routeBeforeCheckout', route.fullPath)
    },
    setFacebookAuth (state, authResponse) {
      state.facebookAuth = authResponse
      setItem('facebookAuth', authResponse)
    },
    setSelectedPage (state, payload) {
      state.selectedPage = payload
      setItem('selectedPage', payload)
    },
    initDonateType (state) {
      const stored = getItem('donateType') || 'onetime'
      const sanitized = gatedDonateType(stored)
      state.donateType = sanitized
      if (sanitized !== stored) setItem('donateType', sanitized)
      // Daily option is build-time gated. When disabled, ignore any stale
      // forceDaily=true left over in localStorage from previous sessions.
      const dailyEnabled = Vue.config.DAILY_OPTION_ENABLED === true || Vue.config.DAILY_OPTION_ENABLED === 'true'
      state.forceDaily = dailyEnabled ? (getItem('forceDaily') || false) : false
      if (!dailyEnabled) setItem('forceDaily', false)
    },
    setDonateType (state, payload) {
      const sanitized = gatedDonateType(payload)
      state.donateType = sanitized
      setItem('donateType', sanitized)
      // Phase 10 (UI-SPEC): onetime has no duration concept; reset to indefinite default.
      if (sanitized === 'onetime') {
        state.totalCount = 0
        setItem('totalCount', 0)
      }
    },
    setForceDaily (state, payload) {
      state.forceDaily = payload
      setItem('forceDaily', payload)
    },
    initTotalCount (state) {
      const raw = getItem('totalCount')
      const n = parseInt(raw, 10)
      state.totalCount = Number.isFinite(n) && n >= 0 ? n : 0
    },
    setTotalCount (state, payload) {
      state.totalCount = payload
      setItem('totalCount', payload)
    }
  },
  actions: {
    setUser ({commit}, user) {
      commit('setUser', user)
    },
    setToken ({commit}, token) {
      commit('setToken', token)
    },
    loadProducts ({dispatch, commit, state}) {
      commit('clearProducts')
      return dispatch('syncProducts')
    },
    syncProducts ({commit}, showUnlisted) {
      return new Promise((resolve, reject) => {
        const payload = {
          params: {
            showUnlisted: showUnlisted
          }
        }
        Vue.http.get('products', payload)
          .then(resp => {
            if (resp.body.products) {
              for (let product of resp.body.products) {
                commit('addProduct', product)
                commit('updateCartProduct', product)
              }
              resolve(resp.body.products)
            } else {
              resolve()
            }
          })
          .catch(err => {
            console.log(err)
            reject(err)
          })
      })
    },
    initUser ({commit}) {
      commit('setUser', getItem('user'))
      commit('setToken', getItem('token'))
    },
    initCart ({commit, dispatch}) {
      commit('initCart')
      // check to force offline shipping
      if (!Vue.prototype.$utils.isCartShippable()) {
        dispatch('setOfflineShipping', true)
      }

      dispatch('initOfflineShipping')
    },
    addToCart ({commit, state, dispatch}, payload) {
      if (payload.quantity > 0) {
        commit('addToCart', payload)
      }
      // check to force offline shipping
      if (!Vue.prototype.$utils.isCartShippable()) {
        dispatch('setOfflineShipping', true)
      }
    },
    removeFromCart ({commit, dispatch}, payload) {
      commit('removeFromCart', payload)
      // check to force offline shipping
      if (!Vue.prototype.$utils.isCartShippable()) {
        dispatch('setOfflineShipping', true)
      }
    },
    clearCart ({dispatch, commit}) {
      commit('clearCart')
      return dispatch('resetPayment')
    },
    clearSelectedPage ({commit}) {
      commit('clearSelectedPage')
    },
    resetPayment ({commit}) {
      commit('setPaymentMethod', null)
      commit('setOrderId', null)
      commit('setOmiseTokenId', null)
      commit('setPaypalPaymentId', null)
    },
    increaseProductQuantity ({commit}, product) {
      commit('increaseProductQuantity', product)
    },
    decreaseProductQuantity ({commit}, product) {
      commit('decreaseProductQuantity', product)
    },
    updateProductQuantity ({commit}, payload) {
      commit('updateProductQuantity', payload)
    },
    initCheckoutInfo ({commit}) {
      commit('updateCheckoutInfo', getItem('checkoutInfo') || {})
    },
    updateCheckoutInfo ({commit}, payload) {
      commit('updateCheckoutInfo', payload)
    },
    initOfflineShipping ({commit}) {
      commit('setOfflineShipping', getItem('offlineShipping') || false)
    },
    setOfflineShipping ({commit}, value) {
      commit('setOfflineShipping', value)
    },
    toggleCartStatus ({commit}, enabled) {
      commit('toggleCartStatus', enabled)
    },
    setToast ({commit}, toast) {
      commit('setToast', toast)
    },
    initWebSocket ({commit}) {
      let clientId = getItem('clientId')
      const params = clientId ? ('?clientID=' + clientId) : ''

      const ws = new ReconnectingWebSocket(`${Vue.config.WS_URL}${params}`, [])

      commit('setWebSocket', ws)
      const handler = (event) => {
        const data = JSON.parse(event.data)
        if (data.type === 'connected') {
          clientId = data.payload
          console.log(`Connected with clientId: ${clientId}`)
          commit('setClientId', clientId)
          ws.removeEventListener('message', handler)
        }
      }

      ws.addEventListener('message', handler)

      ws.addEventListener('close', (event) => {
        console.log('Disconnected')
      })

      ws.addEventListener('error', (event) => {
        console.log('Error connecting to server')
      })
    },
    setPaymentMethod ({commit}, method) {
      commit('setPaymentMethod', method)
    },
    setOmiseTokenId ({commit}, tokenId) {
      commit('setOmiseTokenId', tokenId)
    },
    setOrderId ({commit}, payload) {
      commit('setOrderId', payload)
    },
    setPaypalPaymentId ({commit, state}, paypalPaymentId) {
      commit('setPaypalPaymentId', paypalPaymentId)
      commit('setOrderId', { orderId: state.orderId, publicOrderId: state.publicOrderId })
    },
    logout ({commit}, clearCart) {
      commit('setUser', null)
      commit('setToken', null)
      commit('setFacebookAuth', {})
      if (clearCart) {
        commit('clearCart')
        commit('setOrderId', null)
      }
      commit('updateCheckoutInfo', {})
    },
    setNavFocusMode ({commit}, enabled) {
      commit('setNavFocusMode', enabled)
    },
    setNavTitle ({commit}, title) {
      commit('setNavTitle', title)
    },
    setBackVisibility ({commit}, enabled) {
      commit('setBackVisibility', enabled)
    },
    setPreviousRoute ({commit}, route) {
      commit('setPreviousRoute', route)
    },
    setRouteBeforeCheckout ({commit}, route) {
      commit('setRouteBeforeCheckout', route)
    },
    setFacebookAuth ({commit}, authResponse) {
      commit('setFacebookAuth', authResponse)
    },
    initFacebookAuth ({commit}) {
      const fbAuth = getItem('facebookAuth')
      if (fbAuth) {
        commit('setFacebookAuth', fbAuth)
      }
    },
    setSelectedPage ({commit}, payload) {
      commit('setSelectedPage', payload)
    },
    initDonateType ({commit}) {
      commit('initDonateType')
    },
    setDonateType ({commit}, payload) {
      commit('setDonateType', payload)
    },
    setForceDaily ({commit}, payload) {
      commit('setForceDaily', payload)
    },
    initTotalCount ({commit}) {
      commit('initTotalCount')
    },
    setTotalCount ({commit}, payload) {
      commit('setTotalCount', payload)
    }
  }
})

export default store



// WEBPACK FOOTER //
// ./src/store.js